import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MdOutlineCreditCardOff } from "react-icons/md";
import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Checkmark from "../../components/Checkmark/Checkmark";
import { ProductPicker, getTrialableProducts } from "../../components/ProductPicker/ProductPicker";
import axiosClient from "../../axiosClient";
import PRODUCTS from "../SignUpPage/products";
import { brand, contactEmail, contactMailto, platform, setAccessToken } from "../../brand";
import { marketingUrl } from "../../lib/marketingUrls";
import { usePageTitle } from "../../hooks/usePageTitle";
import {
  PASSWORD_REQUIREMENTS_HINT,
  PASSWORD_VALIDATION_ERROR,
  isPasswordValid,
} from "../../utils/passwordRequirements";
import "../SignUpPage/SignUpPage.scss";
import "./StartTrialPage.scss";

export const TRIAL_DAYS = 3;
export const TRIAL_QUESTIONS = 50;

function StartTrialPage() {
  usePageTitle("Start your free trial");
  const [searchParams] = useSearchParams();
  const trialProducts = getTrialableProducts(PRODUCTS);

  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(() => {
    const requested = searchParams.get("product");
    if (requested && trialProducts.some((p) => p.id === requested)) {
      return new Set([requested]);
    }
    return trialProducts.length === 1 ? new Set([trialProducts[0].id]) : new Set();
  });
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const productId = Array.from(selectedProducts)[0];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!productId) {
      setError("Please choose the question bank you want to trial.");
      return;
    }
    if (!name.trim() || !surname.trim() || !email.trim()) {
      setError("Please fill in your name, surname and email.");
      return;
    }
    if (!isPasswordValid(password)) {
      setError(PASSWORD_VALIDATION_ERROR);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!agreedToTerms) {
      setError("Please agree to the terms to continue.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axiosClient.post("/auth/trial-signup", {
        name: name.trim(),
        surname: surname.trim(),
        email: email.trim(),
        password,
        productId,
        platform,
      });
      setAccessToken(response.data.accessToken);
      window.location.assign("/dashboard");
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.error;
      if (status === 429) {
        setError("Too many attempts. Please wait a while and try again.");
      } else if (message === "Bad request") {
        setError(
          "We couldn't start a free trial with this email. If you already have an account, please log in instead.",
        );
      } else {
        setError(message || "Something went wrong. Please try again.");
      }
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <NavBar />
      <div id="SignUpPage" className="start-trial-page">
        <div className="site-container">
          <div className="order-summary-wrapper">
            <div className="order-summary-inner">
              <div className="inner-cols-wrapper">
                <div className="order-summary-col">
                  <form id="form-wrapper" onSubmit={handleSubmit} noValidate>
                    <h3 className="order-summary-section-title">Start your free trial</h3>
                    <p>Try {TRIAL_QUESTIONS} questions from one question bank free for {TRIAL_DAYS} days.</p>
                    <div>
                      <div className="start-trial-no-card">
                        <MdOutlineCreditCardOff className="start-trial-no-card-icon" aria-hidden="true" />
                        <div>
                          <strong>No card needed</strong>
                          <span>The trial is free. Only upgrade if you decide to.</span>
                        </div>
                      </div>
                    </div>

                    <h5 className="signup-form-heading">Choose a question bank</h5>
                    {trialProducts.length > 0 ? (
                      <ProductPicker
                        products={trialProducts}
                        selected={selectedProducts}
                        onChange={setSelectedProducts}
                        multiple={false}
                        trialOffer={{ days: TRIAL_DAYS, questions: TRIAL_QUESTIONS }}
                      />
                    ) : (
                      <p>No question banks are available for a free trial right now.</p>
                    )}

                    <h4 className="order-summary-section-title">Your details</h4>
                    <div className="user-details-section">
                      <div className="user-details-name-wrapper">
                        <input
                          type="text"
                          name="name"
                          placeholder="Name"
                          autoComplete="given-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                        <input
                          type="text"
                          name="surname"
                          placeholder="Surname"
                          autoComplete="family-name"
                          value={surname}
                          onChange={(e) => setSurname(e.target.value)}
                          required
                        />
                      </div>

                      <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />

                      <p className="signup-form-hint" id="trial-password-requirements">
                        {PASSWORD_REQUIREMENTS_HINT}
                      </p>
                      <input
                        type="password"
                        name="password"
                        placeholder="Password"
                        autoComplete="new-password"
                        minLength={8}
                        aria-describedby="trial-password-requirements"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <input
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirm password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>

                    <div className="signup-terms-row">
                      <input
                        type="checkbox"
                        name="agree_to_terms"
                        id="trial_agree_to_terms"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        required
                      />
                      <label htmlFor="trial_agree_to_terms">
                        I agree to {brand.displayName}&apos;s{" "}
                        <a href={marketingUrl("/terms-and-conditions")} target="_blank" rel="noopener noreferrer">
                          Terms and Conditions
                        </a>
                        ,{" "}
                        <a href={marketingUrl("/terms-of-use")} target="_blank" rel="noopener noreferrer">
                          Terms of Use
                        </a>
                        , and our{" "}
                        <a href={marketingUrl("/privacy-policy")} target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </a>
                        .
                      </label>
                    </div>

                    {error && (
                      <p className="start-trial-error" role="alert">
                        {error}
                      </p>
                    )}

                    <div className="start-trial-actions">
                      <button
                        type="submit"
                        className="start-trial-submit"
                        disabled={isSubmitting || trialProducts.length === 0}
                      >
                        {isSubmitting ? "Starting your trial…" : "Start free trial"}
                      </button>
                      <p className="start-trial-submit-note">
                        <MdOutlineCreditCardOff aria-hidden="true" /> No card needed to start your trial.
                      </p>

                      <p className="start-trial-alt-links">
                        Already have an account? <Link to="/login">Sign in</Link>
                      </p>
                      <p className="start-trial-alt-links">
                        Ready to buy?{" "}
                        <Link to={productId ? `/sign-up?product=${encodeURIComponent(productId)}` : "/sign-up"}>
                          Purchase full access
                        </Link>
                      </p>
                    </div>
                  </form>
                </div>

                <div className="key-features-col">
                  <h3 className="key-features-title">Your free trial</h3>
                  <div className="key-features-list-wrapper">
                    <ul>
                      <li>
                        <Checkmark />
                        {TRIAL_QUESTIONS} questions from your chosen question bank
                      </li>
                      <li>
                        <Checkmark />
                        Lasts {TRIAL_DAYS} days
                      </li>
                      <li>
                        <Checkmark />
                        Quick practice across the whole subject
                      </li>
                      <li>
                        <Checkmark />
                        No card needed. Upgrade from your dashboard at any time
                      </li>
                    </ul>
                  </div>
                  <p id="please-contact-us">
                    Please contact <a href={contactMailto}>{contactEmail}</a> for any support
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default StartTrialPage;
